import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, BellRing, Loader2, MessageCircle } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errMsg } from "../../lib/api";

export const useShare = (teamId) => {
  const [share, setShare] = useState(null);
  const load = () => api.get(`/teams/${teamId}/share`).then((r) => setShare(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { share, reload: load };
};

const RemindResult = ({ r, link }) => {
  if (!r.no_email.length) return null;
  const msg = `Hi! Please add your child's photo and stats for their club shield here: ${link}`;
  return (
    <div className="space-y-2 bg-amber-50 p-3 text-xs" data-testid="squad-remind-no-email">
      <p><b>No email on file for:</b> {r.no_email.join(", ")}. Send them the link another way:</p>
      <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="squad-remind-whatsapp"
        className="inline-flex items-center gap-1.5 bg-[#25D366] px-3 py-1.5 font-bold uppercase text-white"><MessageCircle className="h-3.5 w-3.5" /> Share on WhatsApp</a>
    </div>
  );
};

export const ShareBox = ({ teamId, share, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!share) return null;
  const link = `${window.location.origin}/s/${share.share_code}`;
  const update = async (patch) => {
    const next = { allow_parent_photo: share.allow_parent_photo, allow_parent_stats: share.allow_parent_stats, ...patch };
    try { await api.put(`/teams/${teamId}/share`, next); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/remind`);
      setResult(data);
      const parts = [`${data.sent.length} reminder${data.sent.length === 1 ? "" : "s"} emailed`];
      if (data.recent.length) parts.push(`${data.recent.length} already reminded in the last 24h`);
      if (data.no_email.length) parts.push(`${data.no_email.length} without email`);
      toast.success(parts.join(" · "));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Parent link copied"); } catch { toast.message(link); }
  };
  return (
    <div className="space-y-3 border-t pt-4" data-testid="squad-share-box">
      <div className="flex items-center gap-2 text-sm font-bold"><Link2 className="h-4 w-4" /> Parent link</div>
      <p className="text-xs text-slate-500">Share with parents. Each parent picks their child, adds the photo and statistics, and pays for their own shield. The club look stays locked.</p>
      <div className="flex gap-2">
        <input readOnly value={link} className="font-mono-cc min-w-0 flex-1 border-2 border-slate-200 bg-slate-50 px-2 py-1.5 text-xs" data-testid="squad-share-link" onFocus={(e) => e.target.select()} />
        <button type="button" onClick={copy} className="flex items-center gap-1 bg-slate-900 px-3 text-xs font-bold uppercase text-white hover:bg-slate-700" data-testid="squad-share-copy-button"><Copy className="h-3.5 w-3.5" /> Copy</button>
      </div>
      <label className="flex items-center justify-between text-sm font-semibold">Parents can upload the photo
        <Switch checked={share.allow_parent_photo} onCheckedChange={(v) => update({ allow_parent_photo: v })} data-testid="squad-allow-photo-switch" />
      </label>
      <label className="flex items-center justify-between text-sm font-semibold">Parents can enter statistics
        <Switch checked={share.allow_parent_stats} onCheckedChange={(v) => update({ allow_parent_stats: v })} data-testid="squad-allow-stats-switch" />
      </label>
      <button type="button" onClick={remind} disabled={busy} data-testid="squad-remind-button"
        className="btn-blaze flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Remind parents who haven't finished
      </button>
      {result && <RemindResult r={result} link={link} />}
      <p className="text-xs text-slate-500">If a parent emails you a photo instead, open the player with Edit and upload it yourself.</p>
    </div>
  );
};
